import { Fragment } from 'react';
import { Term } from '../../components/Term';
import type { GlossaryTerm } from '../glossary/terms';

/**
 * The light linkifier for lesson prose.
 *
 * Lesson bodies are plain strings and must stay that way — the curriculum is
 * content, not markup. But every lesson already declares which glossary terms
 * it teaches, so when one of those terms' names appears verbatim in a
 * paragraph, the word itself becomes a tappable <Term>. Nothing is rewritten:
 * the paragraph text survives character for character, some spans just gain
 * an underline and a popover.
 *
 * Kept deliberately quiet: the FIRST occurrence per paragraph per term, whole
 * words only, matched against the term's display title and its existing alias
 * list. A paragraph that mentions "rashi" five times gets one link, not five
 * dotted underlines in a row.
 */

export interface ParagraphSegment {
  text: string;
  /** Present when this span should render as a tappable <Term>. */
  termId?: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Split one paragraph into plain and term-linked spans. Pure and exported so
 * the matching rules are testable without rendering anything.
 */
export function segmentParagraph(
  paragraph: string,
  entries: readonly GlossaryTerm[],
): ParagraphSegment[] {
  interface Hit {
    start: number;
    end: number;
    termId: string;
  }
  const hits: Hit[] = [];
  const linked = new Set<string>();

  // Longest names first, so "kundli milan" wins over a bare "kundli" and the
  // shorter name can still link elsewhere in the paragraph's remaining text.
  const candidates = entries
    .flatMap((entry) =>
      [entry.title, ...(entry.aliases ?? [])].map((name) => ({ id: entry.id, name })),
    )
    .filter((c) => c.name.trim().length >= 2)
    .sort((a, b) => b.name.length - a.name.length);

  const overlaps = (start: number, end: number) =>
    hits.some((hit) => start < hit.end && end > hit.start);

  for (const { id, name } of candidates) {
    if (linked.has(id)) continue;
    // Whole words only: "rashi" must not light up inside "Parashara".
    const re = new RegExp(
      `(?<![A-Za-z0-9])${escapeRegExp(name.trim())}(?![A-Za-z0-9])`,
      'gi',
    );
    for (const match of paragraph.matchAll(re)) {
      const start = match.index ?? -1;
      if (start < 0) continue;
      const end = start + match[0].length;
      if (overlaps(start, end)) continue;
      hits.push({ start, end, termId: id });
      linked.add(id);
      break; // first occurrence only
    }
  }

  hits.sort((a, b) => a.start - b.start);

  const segments: ParagraphSegment[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start > cursor) segments.push({ text: paragraph.slice(cursor, hit.start) });
    segments.push({ text: paragraph.slice(hit.start, hit.end), termId: hit.termId });
    cursor = hit.end;
  }
  if (cursor < paragraph.length) segments.push({ text: paragraph.slice(cursor) });
  if (segments.length === 0) segments.push({ text: paragraph });
  return segments;
}

/** One lesson paragraph with its own terms linked in place. */
export function LinkedParagraph({
  text,
  entries,
}: {
  text: string;
  entries: readonly GlossaryTerm[];
}) {
  return (
    <>
      {segmentParagraph(text, entries).map((segment, i) =>
        segment.termId ? (
          <Term key={i} id={segment.termId}>
            {segment.text}
          </Term>
        ) : (
          <Fragment key={i}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}
